import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { hasUnsavedChanges } from '@/utils/unsavedChanges';
import {
  backWithinRecord,
  closeRecord,
  openRecord,
  openWithinRecord,
  readRecordMark,
  recordState,
  type RecordNavigation,
} from './record-history';
import { useLiveAddress, useOwnHistory } from './live-address';
import { carryViewApplied } from './last-view';

/**
 * Opening and closing a record over a list, for records with their own path (`/runs/:id`).
 * See `record-history.ts` for the history it keeps. Hrefs are path plus query.
 *
 * - `open(to, listHref)`: from the list (pushes), or switching record (replaces). `isOpen` says
 *   whether a record is on screen now.
 * - `openWithin(to)`: a view inside the record, such as edit or history (pushes).
 * - `back(to)`: from that view to the record (Back when it was pushed, otherwise replaces).
 * - `close(listHref)`: Back to the list it was opened from, or replace with the list.
 *
 * None of them ask about unsaved work: confirm first. With an
 * unsaved-changes guard on, its spare history entry is stepped over, so Back doesn't land on it
 * and ask a second time.
 */
export function useRecordHistory(isOpen: boolean) {
  const navigate = useNavigate();
  const location = useLocation();
  const here = `${location.pathname}${location.search}`;
  const ownHistory = useOwnHistory();

  const go = useCallback(
    (nav: RecordNavigation) => {
      // useUnsavedChangesGuard keeps a spare entry for this page on top of the browser's history while
      // it's on (not in the link dialog's own history).
      if ('back' in nav) navigate(-(nav.back + (!ownHistory && hasUnsavedChanges('leave') ? 1 : 0)));
      else {
        // A record opened over a saved view keeps its mark, so the list behind it stays that view.
        const carried = carryViewApplied(location.state);
        const state = { ...carried, ...recordState(nav.mark) };
        navigate(nav.to, { replace: nav.replace, state: Object.keys(state).length ? state : undefined });
      }
    },
    [navigate, location.state, ownHistory]
  );

  // Stable until the URL changes, so a table's columns can hold these without rebuilding each render.
  return useMemo(() => {
    const mark = readRecordMark(location.state);
    return {
      /** Opened from a list in this tab (so closing goes Back). */
      openedInApp: mark?.list != null,
      open: (to: string, listHref: string) => go(openRecord(mark, isOpen, to, listHref)),
      openWithin: (to: string) => go(openWithinRecord(mark, to, here)),
      back: (to: string) => go(backWithinRecord(mark, to)),
      close: (listHref: string) => go(closeRecord(mark, listHref)),
    };
  }, [location.state, isOpen, go, here]);
}

/**
 * A record shown over a list as `?<key>=<id>` (a centred modal): its id, and
 * `open`/`close` with the same history as `useRecordHistory`. Other keys stay as they are.
 *
 * ```tsx
 * const record = useUrlRecord('run');
 * <Dialog open={record.id !== null} onOpenChange={(o) => !o && record.close()}>
 * ```
 */
export function useUrlRecord(key: string) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const id = searchParams.get(key) || null;
  const history = useRecordHistory(id !== null);
  // The address bar, or the link dialog's own router (SF-T-160).
  const liveAddress = useLiveAddress();

  const hrefWith = useCallback(
    (value: string | null, live = false) => {
      // Handlers use the address bar: a write just before the click may not be rendered yet.
      const params = new URLSearchParams(live ? liveAddress().search : searchParams);
      if (value === null) params.delete(key);
      else params.set(key, value);
      const query = params.toString();
      return query ? `${location.pathname}?${query}` : location.pathname;
    },
    [searchParams, key, location.pathname, liveAddress]
  );

  return useMemo(
    () => ({
      id,
      openedInApp: history.openedInApp,
      open: (recordId: string) => history.open(hrefWith(recordId, true), hrefWith(null, true)),
      close: () => history.close(hrefWith(null, true)),
      /** The link to a record, for a real `<Link>` (Cmd-click opens a new tab). */
      href: (recordId: string) => hrefWith(recordId),
    }),
    [id, history, hrefWith]
  );
}

/** What `useUrlRecord` returns: pass it to `RecordLink`. */
export type UrlRecord = ReturnType<typeof useUrlRecord>;
